import { useEffect, useRef } from 'react';

import { handleChunkLoadFailure, isChunkLoadError } from '@/utils/chunkLoadError';

function isStyleOrScriptLoadTarget(target: EventTarget | null): target is HTMLScriptElement | HTMLLinkElement {
  if (!target || !(target instanceof Element)) return false;
  if (target.tagName === 'SCRIPT') return true;
  if (target.tagName !== 'LINK') return false;
  const link = target as HTMLLinkElement;
  const rel = (link.rel || '').toLowerCase();
  // Vite 生产环境会先 preload as=style，再转 stylesheet
  return rel === 'stylesheet' || (rel.includes('preload') && link.as === 'style');
}

function getResourceUrl(target: HTMLScriptElement | HTMLLinkElement): string {
  if (target.tagName === 'SCRIPT') return (target as HTMLScriptElement).src || '';
  return (target as HTMLLinkElement).href || '';
}

/** 仅处理本应用构建产物，避免三方脚本/浏览器扩展失败触发整页刷新 */
function isAppBuildAssetUrl(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin !== window.location.origin) return false;
    return parsed.pathname.startsWith('/assets/');
  } catch {
    return false;
  }
}

/**
 * 全站静态资源 / 动态 chunk 加载失败保底。
 * flag 仅在动态 import 成功时清除（见 importWithChunkReload），不绑 window.load，避免死循环或再也刷不了。
 */
export const useResourceErrorHandler = () => {
  const errorHandlerRef = useRef<((event: ErrorEvent) => void) | null>(null);
  const rejectionHandlerRef = useRef<((event: PromiseRejectionEvent) => void) | null>(null);

  useEffect(() => {
    errorHandlerRef.current = (event: ErrorEvent) => {
      if (isStyleOrScriptLoadTarget(event.target)) {
        const resourceUrl = getResourceUrl(event.target);
        if (resourceUrl && isAppBuildAssetUrl(resourceUrl)) {
          handleChunkLoadFailure(resourceUrl);
          return;
        }
      }

      if (isChunkLoadError(event.error) || isChunkLoadError(event.message)) {
        handleChunkLoadFailure(event.message || String(event.error || ''));
      }
    };

    rejectionHandlerRef.current = (event: PromiseRejectionEvent) => {
      if (!isChunkLoadError(event.reason)) return;

      event.preventDefault();
      handleChunkLoadFailure(event.reason instanceof Error ? event.reason.message : String(event.reason));
    };

    window.addEventListener('error', errorHandlerRef.current, true);
    window.addEventListener('unhandledrejection', rejectionHandlerRef.current);

    return () => {
      if (errorHandlerRef.current) {
        window.removeEventListener('error', errorHandlerRef.current, true);
      }
      if (rejectionHandlerRef.current) {
        window.removeEventListener('unhandledrejection', rejectionHandlerRef.current);
      }
    };
  }, []);
};
